/**
 * Vehicle Summary Card
 *
 * Displays basic vehicle information from RDW data.
 * Shows make, model, year, fuel type, and vehicle class.
 * Shows "Al in voorraad" badge when vehicle exists in inventory.
 */

import { useTranslation } from 'react-i18next';
import { Car, Package } from 'lucide-react';
import type { RdwDraft } from '@/lib/rdwClient';
import type { ExistingVehicleResult } from '../lib/types';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';

interface VehicleSummaryCardProps {
  rdwData: RdwDraft;
  existingVehicle?: ExistingVehicleResult;
  onOpenExistingVehicle?: () => void;
  className?: string;
}

/**
 * Format license plate with dashes (Dutch format)
 */
function formatPlate(plate: string): string {
  // Common Dutch plate formats: XX-XX-XX, XX-XXX-X, X-XXX-XX, etc.
  // For simplicity, just add dashes every 2-3 chars if not already formatted
  const clean = plate.replace(/[^A-Z0-9]/gi, '').toUpperCase();
  if (clean.length === 6) {
    // Try common patterns
    return `${clean.slice(0, 2)}-${clean.slice(2, 4)}-${clean.slice(4, 6)}`;
  }
  return clean;
}

/**
 * Extract year from first registration date
 */
function getYear(dateStr: string | null | undefined): number | null {
  if (!dateStr) return null;
  const year = parseInt(dateStr.substring(0, 4), 10);
  return isNaN(year) ? null : year;
}

/**
 * Map fuel type to display label
 */
function getFuelLabel(fuelType: string | null | undefined): string | null {
  if (!fuelType) return null;
  const normalized = fuelType.toLowerCase();

  if (normalized.includes('benzine')) return 'Benzine';
  if (normalized.includes('diesel')) return 'Diesel';
  if (normalized.includes('elektr') || normalized === 'electric') return 'Elektrisch';
  if (normalized.includes('hybr')) return 'Hybride';
  if (normalized.includes('lpg')) return 'LPG';

  return fuelType;
}

export function VehicleSummaryCard({
  rdwData,
  existingVehicle,
  onOpenExistingVehicle,
  className,
}: VehicleSummaryCardProps) {
  const { t } = useTranslation('common');

  const make = rdwData.make;
  const model = rdwData.model;
  const year = getYear(rdwData.firstRegDate);
  const fuel = getFuelLabel(rdwData.fuelType);
  const plate = formatPlate(rdwData.plate);

  // Check if vehicle exists in inventory
  const hasExistingVehicle =
    existingVehicle !== null && existingVehicle !== undefined && existingVehicle !== 'UNKNOWN';

  // Build vehicle title
  const titleParts: string[] = [];
  if (make) titleParts.push(make);
  if (model) titleParts.push(model);
  const vehicleTitle =
    titleParts.length > 0
      ? titleParts.join(' ')
      : t('calculator.unknownVehicle', 'Onbekend voertuig');

  // Build subtitle parts
  const subtitleParts: string[] = [];
  if (year) subtitleParts.push(String(year));
  if (fuel) subtitleParts.push(fuel);
  if (rdwData.vehicleClass) subtitleParts.push(rdwData.vehicleClass);

  return (
    <div className={cn('rounded-lg border bg-card p-4', className)}>
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
          <Car className="h-5 w-5 text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-foreground truncate">{vehicleTitle}</h3>
            {hasExistingVehicle && (
              <Badge
                variant="secondary"
                className="shrink-0 cursor-pointer hover:bg-secondary/80 transition-colors"
                onClick={onOpenExistingVehicle}
              >
                <Package className="h-3 w-3 mr-1" />
                {t('calculator.inInventory', 'Al in voorraad')}
              </Badge>
            )}
          </div>
          <p className="text-sm font-mono text-muted-foreground mt-0.5">{plate}</p>
          {subtitleParts.length > 0 && (
            <p className="text-sm text-muted-foreground mt-1">{subtitleParts.join(' • ')}</p>
          )}
        </div>
      </div>
    </div>
  );
}
